import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Loader2, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveError, showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import {
  goodsReturnService,
  mapFormLineToGoodsReturnSaveLine,
  type GoodsReturnGrnRow,
  type GoodsReturnTaxRateOption,
  type GoodsReturnVendorOption,
  type GoodsReturnWarehouseOption,
} from '@/services/goodsReturnService'
import { purchaseOrderService } from '@/services/purchaseOrderService'
import { itemService } from '@/services/itemService'
import { batchItemService } from '@/services/batchItemService'

import {
  type GoodsReturnNoteLine,
  lineTaxAmount,
  lineTaxableAmount,
  lineTotal,
} from '../types'

const today = () => new Date().toISOString().slice(0, 10)

const PLACEHOLDER = '__none__'

function vendorLabel(v: GoodsReturnVendorOption, lang: string) {
  const useAr = String(lang).toLowerCase().startsWith('ar')
  if (useAr) return v.vendorNameAr || v.vendorNameEn || String(v.vendorId)
  return v.vendorNameEn || v.vendorNameAr || String(v.vendorId)
}

function warehouseLabel(w: GoodsReturnWarehouseOption) {
  const c = w.code.trim()
  const n = w.name.trim()
  if (c && n) return `${c} — ${n}`
  return n || c || String(w.id)
}

function taxRateLabel(r: GoodsReturnTaxRateOption, lang: string) {
  const useAr = String(lang).toLowerCase().startsWith('ar')
  const name = useAr ? r.taxRateNameAr || r.taxRateNameEn : r.taxRateNameEn || r.taxRateNameAr
  const label = name.trim() || String(r.id)
  return `${label} (${r.percent}%)`
}

function numField(raw: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v)
  }
  return 0
}

/** GRN line fields vary by serializer — try common aliases. */
function grnLineQuantity(row: Record<string, unknown>): number {
  return numField(row, [
    'quantity',
    'Quantity',
    'qty',
    'Qty',
    'receivedQuantity',
    'ReceivedQuantity',
    'quantityReceived',
    'QuantityReceived',
    'grnQuantity',
    'GrnQuantity',
  ])
}

function grnLineQuantityReturned(row: Record<string, unknown>): number {
  return numField(row, ['quantityReturned', 'QuantityReturned', 'qtyReturned', 'QtyReturned'])
}

function grnLineUnitPrice(row: Record<string, unknown>): number {
  return numField(row, [
    'unitPrice',
    'UnitPrice',
    'price',
    'Price',
    'rate',
    'Rate',
    'unitCost',
    'UnitCost',
    'costPrice',
    'CostPrice',
    'lineUnitPrice',
    'LineUnitPrice',
  ])
}

function grnLineDiscountPercent(row: Record<string, unknown>): number {
  return numField(row, [
    'discount',
    'Discount',
    'discountPercent',
    'DiscountPercent',
    'lineDiscountPercent',
    'LineDiscountPercent',
    'discountPct',
    'DiscountPct',
  ])
}

function strField(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

function orderIdFromHeader(h: Record<string, unknown>): number {
  const v = h.orderId ?? h.OrderId
  if (v == null || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export const GoodsReturnNotesEdit: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { i18n } = useTranslation('common')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const userId = useAuthStore((s) => s.user?.userId ?? 0)

  const existingReturnId =
    routeId && routeId !== 'new' && Number.isFinite(Number(routeId)) && Number(routeId) > 0
      ? Number(routeId)
      : 0
  const isEditMode = existingReturnId > 0

  const [grns, setGrns] = useState<GoodsReturnGrnRow[]>([])
  const [vendors, setVendors] = useState<GoodsReturnVendorOption[]>([])
  const [warehouses, setWarehouses] = useState<GoodsReturnWarehouseOption[]>([])
  const [taxRates, setTaxRates] = useState<GoodsReturnTaxRateOption[]>([])
  const [listsLoading, setListsLoading] = useState(false)
  const [grnApplying, setGrnApplying] = useState(false)
  const [editInitializing, setEditInitializing] = useState(false)

  const [returnCode, setReturnCode] = useState('')
  const [loadedGrnCode, setLoadedGrnCode] = useState('')
  const [savedAttachmentPath, setSavedAttachmentPath] = useState('')
  const [persistedCreatedDate, setPersistedCreatedDate] = useState('')
  const [persistedCreatedBy, setPersistedCreatedBy] = useState<number | null>(null)
  const [persistedStatus, setPersistedStatus] = useState(0)
  const [persistedBranchId, setPersistedBranchId] = useState<number | null>(null)
  const [persistedIsBillGenerated, setPersistedIsBillGenerated] = useState(false)
  const [persistedIsReturnCreated, setPersistedIsReturnCreated] = useState(true)

  const [vendorId, setVendorId] = useState(PLACEHOLDER)
  const [grnId, setGrnId] = useState(PLACEHOLDER)
  const [orderId, setOrderId] = useState(PLACEHOLDER)
  const [orderCode, setOrderCode] = useState('')
  const [warehouseId, setWarehouseId] = useState(PLACEHOLDER)
  const [grnDate, setGrnDate] = useState('')

  const [returnDate, setReturnDate] = useState(today())
  const [returnReference, setReturnReference] = useState('')
  const [attachmentName, setAttachmentName] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)

  const [lines, setLines] = useState<GoodsReturnNoteLine[]>([])

  const totals = useMemo(() => {
    const sub = lines.reduce((s, l) => s + lineTaxableAmount(l), 0)
    const tax = lines.reduce((s, l) => s + lineTaxAmount(l), 0)
    const net = lines.reduce((s, l) => s + lineTotal(l), 0)
    return {
      subTotal: Math.round(sub * 10000) / 10000,
      totalTax: Math.round(tax * 10000) / 10000,
      netAmount: Math.round(net * 10000) / 10000,
    }
  }, [lines])

  const updateLine = (lineId: string, patch: Partial<GoodsReturnNoteLine>) => {
    setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...patch } : l)))
  }

  useEffect(() => {
    setGrnId(PLACEHOLDER)
    setVendorId(PLACEHOLDER)
    setWarehouseId(PLACEHOLDER)
    setOrderId(PLACEHOLDER)
    setOrderCode('')
    setGrnDate('')
    setLines([])
    setAttachmentFile(null)
    setAttachmentName('')
    setReturnCode('')
    setLoadedGrnCode('')
    setSavedAttachmentPath('')
    setPersistedCreatedDate('')
    setPersistedCreatedBy(null)
    setPersistedStatus(0)
    setPersistedBranchId(null)
    setPersistedIsBillGenerated(false)
    setPersistedIsReturnCreated(true)
    setReturnDate(today())
    setReturnReference('')
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setGrns([])
      setVendors([])
      setWarehouses([])
      setTaxRates([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setListsLoading(true)
        const [g, v, w, t] = await Promise.all([
          goodsReturnService.getGrns(branchId),
          goodsReturnService.getAllVendors(branchId),
          goodsReturnService.getAllWarehouses(branchId),
          goodsReturnService.getAllTaxRates(),
        ])
        if (!cancelled) {
          setGrns(g)
          setVendors(v)
          setWarehouses(w)
          setTaxRates(t)
        }
      } catch {
        if (!cancelled) {
          toast.error('Failed to load lookups (GRNs, vendors, warehouses, or tax rates).')
          setGrns([])
          setVendors([])
          setWarehouses([])
          setTaxRates([])
        }
      } finally {
        if (!cancelled) setListsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    if (!isEditMode || !existingReturnId || !branchId || listsLoading) {
      if (!isEditMode) setEditInitializing(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setEditInitializing(true)
        const [h, rawLines] = await Promise.all([
          goodsReturnService.getGoodsReturnById(existingReturnId),
          goodsReturnService.getGoodsReturnLinesByReturnId(existingReturnId),
        ])
        if (cancelled) return
        if (!h || Object.keys(h).length === 0) {
          toast.error('Goods return not found.')
          navigate('/inventory/goods-return-notes')
          return
        }
        const hdr = h as Record<string, unknown>
        const gid = numField(hdr, ['grnId', 'GrnId'])
        const oid = orderIdFromHeader(hdr)
        const poc = strField(hdr, [
          'purchaseorderCode',
          'PurchaseorderCode',
          'purchaseOrderCode',
          'PurchaseOrderCode',
        ])
        const grnCodeStr = strField(hdr, ['grnCode', 'GrnCode'])
        setLoadedGrnCode(grnCodeStr)
        setReturnCode(strField(hdr, ['code', 'Code']))
        setReturnReference(strField(hdr, ['reference', 'Reference']))
        const dateStr = strField(hdr, ['date', 'Date'])
        setReturnDate(dateStr ? dateStr.slice(0, 10) : today())
        setGrnDate(dateStr ? dateStr.slice(0, 10) : '')
        setVendorId(String(numField(hdr, ['vendorId', 'VendorId']) || PLACEHOLDER))
        setWarehouseId(String(numField(hdr, ['warehouseId', 'WarehouseId']) || PLACEHOLDER))
        setGrnId(gid > 0 ? String(gid) : PLACEHOLDER)

        setGrns((prev) => {
          if (gid <= 0) return prev
          if (prev.some((g) => g.id === gid)) return prev
          const venId = numField(hdr, ['vendorId', 'VendorId'])
          const whId = numField(hdr, ['warehouseId', 'WarehouseId'])
          return [
            ...prev,
            {
              id: gid,
              code: grnCodeStr || `GRN #${gid}`,
              reference: '',
              purchaseOrderId: oid,
              date: dateStr ? dateStr.slice(0, 10) : '',
              vendorId: venId,
              warehouseId: whId,
            },
          ]
        })

        if (oid > 0) {
          setOrderId(String(oid))
          try {
            const po = await purchaseOrderService.getById(oid)
            if (!cancelled) setOrderCode(po.code?.trim() || `PO #${oid}`)
          } catch {
            if (!cancelled) setOrderCode(poc || `PO #${oid}`)
          }
        } else {
          setOrderId(PLACEHOLDER)
          if (!cancelled) setOrderCode(poc)
        }

        const att = strField(hdr, ['attachmentPath', 'AttachmentPath'])
        setSavedAttachmentPath(att)
        const base = att.split(/[/\\]/).pop()
        setAttachmentName(base || '')
        setAttachmentFile(null)

        setPersistedCreatedDate(strField(hdr, ['createdDate', 'CreatedDate']))
        const crBy = numField(hdr, ['createdBy', 'CreatedBy'])
        setPersistedCreatedBy(Number.isFinite(crBy) ? crBy : null)
        setPersistedStatus(numField(hdr, ['status', 'Status']))
        const br = numField(hdr, ['branchId', 'BranchId'])
        setPersistedBranchId(br > 0 ? br : branchId ?? null)
        const ibg = hdr.isBillGenerated ?? hdr.IsBillGenerated
        setPersistedIsBillGenerated(
          ibg === true || ibg === 1 || String(ibg).toLowerCase() === 'true',
        )
        const irc = hdr.isReturnCreated ?? hdr.IsReturnCreated
        if (irc === false || irc === 0) setPersistedIsReturnCreated(false)
        else if (irc === true || irc === 1 || String(irc).toLowerCase() === 'true') {
          setPersistedIsReturnCreated(true)
        }

        const itemLabelCache = new Map<number, string>()
        const batchLabelCache = new Map<number, string>()
        const lang = i18n.language

        const getItemLabel = async (itemId: number): Promise<string> => {
          if (itemId <= 0) return '—'
          if (itemLabelCache.has(itemId)) return itemLabelCache.get(itemId)!
          try {
            const it = await itemService.getItemById(itemId, branchId)
            const useAr = String(lang).toLowerCase().startsWith('ar')
            const name = useAr ? it.itemNameAr || it.itemNameEn : it.itemNameEn || it.itemNameAr
            const code = (it.itemCode || '').trim()
            const label =
              code && name.trim() ? `${code} — ${name.trim()}` : name.trim() || code || `Item #${itemId}`
            itemLabelCache.set(itemId, label)
            return label
          } catch {
            const fb = `Item #${itemId}`
            itemLabelCache.set(itemId, fb)
            return fb
          }
        }

        const getBatchLabel = async (batchItemId: number): Promise<string> => {
          if (batchItemId <= 0) return '—'
          if (batchLabelCache.has(batchItemId)) return batchLabelCache.get(batchItemId)!
          try {
            const b = await batchItemService.getById(batchItemId)
            const code = (b.code || '').trim()
            const ref = (b.reference || '').trim()
            const label = [code, ref].filter(Boolean).join(' — ') || code || ref || `Batch #${batchItemId}`
            batchLabelCache.set(batchItemId, label)
            return label
          } catch {
            const fb = `Batch #${batchItemId}`
            batchLabelCache.set(batchItemId, fb)
            return fb
          }
        }

        const mapped: GoodsReturnNoteLine[] = []
        for (const row of rawLines) {
          const itemId = numField(row, ['itemId', 'ItemId'])
          const batchId = numField(row, ['batchId', 'BatchId'])
          const [itemLabel, batchLabel] = await Promise.all([getItemLabel(itemId), getBatchLabel(batchId)])
          const linePk = numField(row, ['id', 'Id'])
          const qty = numField(row, ['quantity', 'Quantity'])
          const unitPrice = grnLineUnitPrice(row)
          const discPct = grnLineDiscountPercent(row)
          const taxRateIdNum = numField(row, ['tax', 'Tax', 'taxRateId', 'TaxRateId'])
          const taxPctFromRow = numField(row, ['taxPercent', 'TaxPercent'])
          const rateById = taxRates.find((r) => r.id === taxRateIdNum)
          const taxRateIdStr = taxRateIdNum > 0 ? String(taxRateIdNum) : ''
          const resolvedTaxPercent = rateById ? rateById.percent : taxPctFromRow
          mapped.push({
            id: `${linePk}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            goodsReturnLineId: linePk > 0 ? linePk : undefined,
            itemId: itemId > 0 ? itemId : undefined,
            batchId: batchId > 0 ? batchId : undefined,
            item: itemLabel,
            batch: batchLabel,
            grnQuantity: qty,
            quantityReturned: qty,
            unitPrice,
            discountPercent: discPct,
            taxRateId: taxRateIdStr,
            taxPercent: resolvedTaxPercent,
          })
        }
        if (!cancelled) setLines(mapped)
      } catch {
        if (!cancelled) {
          toast.error('Failed to load goods return for edit.')
          navigate('/inventory/goods-return-notes')
        }
      } finally {
        if (!cancelled) setEditInitializing(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isEditMode, existingReturnId, branchId, listsLoading, taxRates, i18n.language, navigate])

  const applyGrnSelection = useCallback(
    async (value: string) => {
      setGrnId(value)
      if (value === PLACEHOLDER) {
        setVendorId(PLACEHOLDER)
        setWarehouseId(PLACEHOLDER)
        setOrderId(PLACEHOLDER)
        setOrderCode('')
        setGrnDate('')
        setLines([])
        return
      }
      if (!branchId) {
        toast.error('Select a branch first.')
        return
      }
      const gid = Number(value)
      const listGrn = grns.find((g) => g.id === gid)
      if (!listGrn) {
        toast.error('GRN not found in list.')
        return
      }

      setGrnApplying(true)
      setLines([])
      try {
        const { grn: grnApi, lines: rawLines } = await goodsReturnService.getGrnLinesForReturn(gid)
        const header = grnApi ?? listGrn

        setVendorId(String(header.vendorId))
        setWarehouseId(String(header.warehouseId))
        setGrnDate(header.date ? header.date.slice(0, 10) : '')

        const poId = header.purchaseOrderId
        if (poId > 0) {
          try {
            const po = await purchaseOrderService.getById(poId)
            setOrderId(String(poId))
            setOrderCode(po.code?.trim() || `PO #${poId}`)
          } catch {
            setOrderId(String(poId))
            setOrderCode(`PO #${poId}`)
            toast.error('Could not load purchase order details; showing PO id only.')
          }
        } else {
          setOrderId(PLACEHOLDER)
          setOrderCode('')
        }

        const itemLabelCache = new Map<number, string>()
        const batchLabelCache = new Map<number, string>()
        const lang = i18n.language

        const getItemLabel = async (itemId: number): Promise<string> => {
          if (itemId <= 0) return '—'
          if (itemLabelCache.has(itemId)) return itemLabelCache.get(itemId)!
          try {
            const it = await itemService.getItemById(itemId, branchId)
            const useAr = String(lang).toLowerCase().startsWith('ar')
            const name = useAr ? it.itemNameAr || it.itemNameEn : it.itemNameEn || it.itemNameAr
            const code = (it.itemCode || '').trim()
            const label =
              code && name.trim() ? `${code} — ${name.trim()}` : name.trim() || code || `Item #${itemId}`
            itemLabelCache.set(itemId, label)
            return label
          } catch {
            const fb = `Item #${itemId}`
            itemLabelCache.set(itemId, fb)
            return fb
          }
        }

        const getBatchLabel = async (batchItemId: number): Promise<string> => {
          if (batchItemId <= 0) return '—'
          if (batchLabelCache.has(batchItemId)) return batchLabelCache.get(batchItemId)!
          try {
            const b = await batchItemService.getById(batchItemId)
            const code = (b.code || '').trim()
            const ref = (b.reference || '').trim()
            const label = [code, ref].filter(Boolean).join(' — ') || code || ref || `Batch #${batchItemId}`
            batchLabelCache.set(batchItemId, label)
            return label
          } catch {
            const fb = `Batch #${batchItemId}`
            batchLabelCache.set(batchItemId, fb)
            return fb
          }
        }

        const mapped: GoodsReturnNoteLine[] = []
        for (const row of rawLines) {
          const itemId = numField(row, ['itemId', 'ItemId'])
          const batchId = numField(row, ['batchId', 'BatchId'])
          const [itemLabel, batchLabel] = await Promise.all([getItemLabel(itemId), getBatchLabel(batchId)])
          const lineApiId = numField(row, ['id', 'Id'])
          const qty = grnLineQuantity(row)
          const qtyRetFromApi = grnLineQuantityReturned(row)
          const unitPrice = grnLineUnitPrice(row)
          const discPct = grnLineDiscountPercent(row)
          const taxRateIdNum = numField(row, ['tax', 'Tax', 'taxRateId', 'TaxRateId'])
          const taxPctFromRow = numField(row, ['taxPercent', 'TaxPercent'])
          const rateById = taxRates.find((r) => r.id === taxRateIdNum)
          const taxRateIdStr = taxRateIdNum > 0 ? String(taxRateIdNum) : ''
          const resolvedTaxPercent = rateById ? rateById.percent : taxPctFromRow
          const quantityReturnedInitial =
            qtyRetFromApi > 0 ? qtyRetFromApi : qty > 0 ? qty : 0
          mapped.push({
            id: `${lineApiId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            lineApiId: lineApiId > 0 ? lineApiId : undefined,
            itemId: itemId > 0 ? itemId : undefined,
            batchId: batchId > 0 ? batchId : undefined,
            item: itemLabel,
            batch: batchLabel,
            grnQuantity: qty,
            quantityReturned: quantityReturnedInitial,
            unitPrice,
            discountPercent: discPct,
            taxRateId: taxRateIdStr,
            taxPercent: resolvedTaxPercent,
          })
        }
        setLines(mapped)
        if (rawLines.length === 0) {
          toast('This GRN has no lines.', { icon: 'ℹ️' })
        }
      } catch {
        toast.error('Failed to load GRN lines.')
        setLines([])
      } finally {
        setGrnApplying(false)
      }
    },
    [grns, branchId, i18n.language, taxRates],
  )

  const lookupsDisabled = !branchId || listsLoading || editInitializing

  const vendorDisplayText = useMemo(() => {
    if (vendorId === PLACEHOLDER) return ''
    const v = vendors.find((x) => String(x.vendorId) === vendorId)
    return v ? vendorLabel(v, i18n.language) : ''
  }, [vendorId, vendors, i18n.language])

  const warehouseDisplayText = useMemo(() => {
    if (warehouseId === PLACEHOLDER) return ''
    const w = warehouses.find((x) => String(x.id) === warehouseId)
    return w ? warehouseLabel(w) : ''
  }, [warehouseId, warehouses])

  const orderDisplayText =
    orderId === PLACEHOLDER ? '' : orderCode.trim() || `PO #${orderId}`

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Select a branch first.')
      return
    }
    if (grnId === PLACEHOLDER) {
      toast.error('Select a goods receipt note.')
      return
    }
    if (lines.length === 0) {
      toast.error('No lines to save.')
      return
    }
    const missing = lines.some((l) => !l.itemId || !l.batchId)
    if (missing) {
      toast.error('Each line must have item and batch from the GRN.')
      return
    }
    const gid = Number(grnId)
    const selectedGrn = grns.find((g) => g.id === gid)
    const grnCodeStr = selectedGrn?.code?.trim() || loadedGrnCode.trim()
    const ref = returnReference.trim()
    const headerDiscount = lines.reduce((s, l) => {
      const q = Math.max(0, Number(l.quantityReturned) || 0)
      const p = Math.max(0, Number(l.unitPrice) || 0)
      const d = Math.max(0, Math.min(100, Number(l.discountPercent) || 0))
      return s + q * p * (d / 100)
    }, 0)
    const now = new Date().toISOString()
    const oid = orderId === PLACEHOLDER ? 0 : Number(orderId)
    const vid = vendorId === PLACEHOLDER ? 0 : Number(vendorId)
    const wid = warehouseId === PLACEHOLDER ? 0 : Number(warehouseId)
    const itemList = lines.map((l) => mapFormLineToGoodsReturnSaveLine(l, existingReturnId))

    try {
      setSaving(true)
      const codeOut = isEditMode ? returnCode.trim() || ref : ref || ''
      const payload = {
        id: isEditMode ? existingReturnId : 0,
        code: codeOut,
        purchaseorderCode: orderCode.trim(),
        grnCode: grnCodeStr,
        reference: ref,
        vendorName: vendorDisplayText,
        warehouseName: warehouseDisplayText,
        grnId: gid,
        orderId: Number.isFinite(oid) ? oid : 0,
        date: returnDate,
        vendorId: Number.isFinite(vid) ? vid : 0,
        warehouseId: Number.isFinite(wid) ? wid : 0,
        attachmentPath: attachmentFile ? '' : savedAttachmentPath,
        taxableAmount: totals.subTotal,
        taxAmount: totals.totalTax,
        discount: Math.round(headerDiscount * 10000) / 10000,
        totalAmount: totals.netAmount,
        isBillGenerated: isEditMode ? persistedIsBillGenerated : false,
        isReturnCreated: isEditMode ? persistedIsReturnCreated : true,
        status: isEditMode ? persistedStatus : 0,
        branchId:
          isEditMode && persistedBranchId != null && persistedBranchId > 0 ? persistedBranchId : branchId,
        createdDate: isEditMode && persistedCreatedDate ? persistedCreatedDate : now,
        createdBy: isEditMode && persistedCreatedBy !== null ? persistedCreatedBy : userId,
        modifiedDate: now,
        modifiedBy: userId,
        attachmentFile,
        itemList,
      }
      if (isEditMode) {
        await goodsReturnService.update(payload)
        await showSaveSuccess(true, 'Goods return note has been updated.')
      } else {
        await goodsReturnService.save(payload)
        await showSaveSuccess(false, 'Goods return note has been saved.')
      }
      navigate('/inventory/goods-return-notes')
    } catch (e: unknown) {
      await showSaveError(e, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Goods Return Notes</h1>
            <p className="text-muted-foreground text-sm">Goods Return Note Details</p>
          </div>
        </div>
      </div>

      {!branchId ? (
        <p className="text-sm text-muted-foreground">Select a branch to load goods receipt notes and lookups.</p>
      ) : null}

      <Card>
        <CardContent className="space-y-8 pt-6">
          <section className="space-y-4">
            <h2 className="text-lg font-semibold">GRN Details</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Goods Receipt Note</Label>
                <Select
                  value={grnId}
                  onValueChange={(v) => void applyGrnSelection(v)}
                  disabled={lookupsDisabled || grnApplying || isEditMode}
                >
                  <SelectTrigger className="bg-muted/40">
                    <SelectValue placeholder={grnApplying ? 'Loading…' : 'Select GRN'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PLACEHOLDER} disabled>
                      Select GRN
                    </SelectItem>
                    {grns.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.code.trim() || `GRN #${g.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  readOnly
                  tabIndex={-1}
                  className="cursor-default bg-muted/40"
                  value={vendorDisplayText}
                  placeholder="—"
                />
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  readOnly
                  tabIndex={-1}
                  className="cursor-default bg-muted/40"
                  value={orderDisplayText}
                  placeholder="—"
                />
              </div>
              <div className="space-y-2">
                <Label>Warehouse</Label>
                <Input
                  readOnly
                  tabIndex={-1}
                  className="cursor-default bg-muted/40"
                  value={warehouseDisplayText}
                  placeholder="—"
                />
              </div>
              <div className="space-y-2">
                <Label>Grn Date</Label>
                <Input
                  type="date"
                  readOnly
                  tabIndex={-1}
                  className="cursor-default bg-muted/40"
                  value={grnDate}
                  placeholder="—"
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 border-t pt-6">
            <h2 className="text-lg font-semibold">Goods Return Details</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {isEditMode ? (
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    readOnly
                    tabIndex={-1}
                    className="cursor-default bg-muted/40"
                    value={returnCode}
                    placeholder="—"
                  />
                </div>
              ) : null}
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  className="bg-muted/40"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input
                  className="bg-muted/40"
                  value={returnReference}
                  onChange={(e) => setReturnReference(e.target.value)}
                  placeholder="Reference"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goods-return-attachment">File</Label>
                <div
                  role="button"
                  tabIndex={0}
                  className="flex cursor-pointer overflow-hidden rounded-md border border-input bg-muted/40 outline-none hover:bg-muted/55 focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => attachmentInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      attachmentInputRef.current?.click()
                    }
                  }}
                >
                  <span className="min-h-10 flex-1 truncate px-3 py-2 text-sm text-muted-foreground">
                    {attachmentName || 'Choose file'}
                  </span>
                  <span className="inline-flex items-center border-l border-input bg-background px-3 text-sm font-medium hover:bg-muted/60">
                    Browse
                  </span>
                  <input
                    ref={attachmentInputRef}
                    id="goods-return-attachment"
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null
                      setAttachmentFile(f)
                      setAttachmentName(f?.name ?? '')
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          <Card className="border-t shadow-none">
            <CardHeader className="bg-primary/5">
              <CardTitle className="text-lg">Order Items</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1240px]">
                  <thead>
                    <tr className="border-b bg-primary/5">
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Item</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Batch</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Grn qty
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Qty returned
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Unit price
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Disc %
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Taxable amt
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Tax rate
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                        Tax amt
                      </th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grnApplying || editInitializing ? (
                      <tr>
                        <td colSpan={10} className="p-10 text-center text-muted-foreground">
                          <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" aria-hidden />
                          {editInitializing ? 'Loading return…' : 'Loading GRN lines…'}
                        </td>
                      </tr>
                    ) : lines.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-10 text-center text-muted-foreground">
                          {isEditMode
                            ? 'No line items on this return.'
                            : 'Select a goods receipt note to load lines.'}
                        </td>
                      </tr>
                    ) : (
                      lines.map((l) => (
                        <tr
                          key={l.id}
                          className="border-b transition-colors hover:bg-muted/40 dark:hover:bg-muted/20"
                        >
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 items-center">
                              <Input
                                readOnly
                                tabIndex={-1}
                                className="min-w-[200px] cursor-default bg-muted"
                                value={l.item || '—'}
                                title={l.item || undefined}
                              />
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 items-center">
                              <Input
                                readOnly
                                tabIndex={-1}
                                className="h-10 min-w-[120px] cursor-default bg-muted"
                                value={l.batch || '—'}
                              />
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 items-center">
                              <Input
                                readOnly
                                className="h-10 w-24 cursor-default bg-muted"
                                value={String(l.grnQuantity)}
                              />
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 items-center">
                              <Input
                                type="number"
                                min={0}
                                className="h-10 w-24"
                                value={l.quantityReturned || ''}
                                onChange={(e) =>
                                  updateLine(l.id, { quantityReturned: Number(e.target.value) || 0 })
                                }
                              />
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 items-center">
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                className="h-10 w-28"
                                value={l.unitPrice || ''}
                                onChange={(e) => updateLine(l.id, { unitPrice: Number(e.target.value) || 0 })}
                              />
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <Input
                              readOnly
                              tabIndex={-1}
                              className="h-10 w-20 cursor-default bg-muted"
                              value={String(l.discountPercent)}
                            />
                          </td>
                          <td className="p-3 align-middle">
                            <Input
                              readOnly
                              className="h-10 w-28 bg-muted"
                              value={lineTaxableAmount(l).toFixed(2)}
                            />
                          </td>
                          <td className="p-3 align-middle">
                            <div className="flex min-h-10 min-w-[200px] items-center">
                              <Select
                                value={l.taxRateId ? l.taxRateId : PLACEHOLDER}
                                onValueChange={(v) => {
                                  if (v === PLACEHOLDER) {
                                    updateLine(l.id, { taxRateId: '', taxPercent: 0 })
                                    return
                                  }
                                  const rate = taxRates.find((r) => String(r.id) === v)
                                  updateLine(l.id, {
                                    taxRateId: v,
                                    taxPercent: rate?.percent ?? 0,
                                  })
                                }}
                                disabled={grnApplying || editInitializing || taxRates.length === 0}
                              >
                                <SelectTrigger className="h-10 w-full">
                                  <SelectValue
                                    placeholder={taxRates.length === 0 ? 'No tax rates' : 'Select tax'}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={PLACEHOLDER} disabled>
                                    Select tax
                                  </SelectItem>
                                  {taxRates.map((r) => (
                                    <SelectItem key={r.id} value={String(r.id)}>
                                      {taxRateLabel(r, i18n.language)}
                                    </SelectItem>
                                  ))}
                                  {l.taxRateId &&
                                  !taxRates.some((r) => String(r.id) === l.taxRateId) ? (
                                    <SelectItem value={l.taxRateId}>
                                      Rate #{l.taxRateId} ({l.taxPercent}%)
                                    </SelectItem>
                                  ) : null}
                                </SelectContent>
                              </Select>
                            </div>
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="h-10 w-28 bg-muted" value={lineTaxAmount(l).toFixed(2)} />
                          </td>
                          <td className="p-3 align-middle">
                            <Input readOnly className="h-10 w-28 bg-muted" value={lineTotal(l).toFixed(2)} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 rounded-md bg-primary/5 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                    <p className="text-lg font-semibold">{totals.subTotal.toFixed(2)}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">TOTAL TAX</Label>
                    <p className="text-lg font-semibold">{totals.totalTax.toFixed(2)}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">NET AMOUNT</Label>
                    <p className="text-lg font-bold">{totals.netAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 border-t pt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/inventory/goods-return-notes')}>
              Cancel
            </Button>
            <Button type="button" disabled={saving || editInitializing} onClick={() => void handleSave()}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? (isEditMode ? 'Updating…' : 'Saving…') : isEditMode ? 'Update' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
